import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, ImageOff, Trash2 } from "lucide-react";
import Card, { Kicker } from "../../brand/Surface";
import Action from "../../brand/Action";
import { useCollection } from "./useCollection";
import { Area, Broken, Field, Loading, Reorder, SaveBar, slug } from "./parts";
import { cn } from "../../../lib/utils";

/**
 * The projects editor.
 *
 * The cover is converted to webp in the browser before it is uploaded. A canvas
 * does that in four lines, which is four lines against adding an image encoder
 * to the dependency list.
 */

const EMPTY = {
  id: "",
  name: "",
  year: String(new Date().getFullYear()),
  description: "",
  url: "",
  tags: [],
  cover: "",
};

/**
 * Down-scale and re-encode to webp.
 *
 * 1200px wide is twice the widest a card is ever drawn at, which covers a 2x
 * screen and nothing beyond it. Quality 0.82 is where webp stops getting
 * visibly better and keeps getting bigger.
 */
function toWebp(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error("kon de afbeelding niet lezen"));
    img.onload = () => {
      const scale = Math.min(1, 1200 / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/webp", 0.82));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}

export default function ProjectsPanel() {
  const { rows, setRows, dirty, status, failed, save, swap } = useCollection(
    "projects",
    "projects",
  );

  if (failed) return <Broken message={failed} />;
  if (!rows) return <Loading />;

  const patch = (i, next) => setRows(rows.map((p, n) => (n === i ? { ...p, ...next } : p)));

  /* Fill in any id nobody typed, from the name. Two projects with the same
     slug would overwrite each other's cover, so they are made unique here
     rather than discovered later. */
  const prepare = (list) => {
    const seen = new Set();
    return list.map((p) => {
      let id = p.id || slug(p.name);
      while (seen.has(id)) id = `${id}-2`;
      seen.add(id);
      return { ...p, id, tags: (p.tags ?? []).filter(Boolean) };
    });
  };

  return (
    <div>
      <SaveBar
        addLabel="Project"
        onAdd={() => setRows([{ ...EMPTY }, ...rows])}
        onSave={() => save(prepare)}
        dirty={dirty}
        status={status}
        count={rows.length}
        countLabel="projecten"
      />

      <div className="flex flex-col gap-3">
        {rows.map((project, i) => (
          <Row
            key={`${project.id}-${i}`}
            project={project}
            index={i}
            count={rows.length}
            onEdit={(next) => patch(i, next)}
            onMove={(by) => setRows(swap(rows, i, by))}
            onRemove={() => setRows(rows.filter((_, n) => n !== i))}
          />
        ))}
      </div>

      <Covers projects={rows} />
    </div>
  );
}

/**
 * Every cover file on disk, and what still uses it.
 *
 * Deleting a project or renaming one leaves its .webp behind. Nothing points
 * at it any more, but `require.context` bundles the whole folder, so the file
 * still ships — invisible weight that only grows. This lists the lot, marks
 * the orphans and lets you delete them.
 *
 * It re-reads after every save, because what counts as an orphan changes the
 * moment you save a project with a different cover.
 */
function Covers({ projects }) {
  const [covers, setCovers] = useState([]);

  const load = () =>
    fetch("/__studio/covers")
      .then((r) => r.json())
      .then((d) => setCovers(d.covers ?? []))
      .catch(() => setCovers([]));

  useEffect(() => {
    load();
  }, [projects]);

  const drop = async (file) => {
    await fetch("/__studio/cover", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ file }),
    });
    load();
  };

  const orphans = covers.filter((c) => !c.used);
  const wasted = orphans.reduce((n, c) => n + c.bytes, 0);

  if (!covers.length) return null;

  return (
    <section className="mt-12">
      <Card pad="lg">
        <Kicker>Afbeeldingen</Kicker>
        <h2 className="text-[22px]">
          {orphans.length
            ? `${orphans.length} ongebruikt, ${Math.round(wasted / 1024)} kB`
            : "Alles in gebruik"}
        </h2>
        <p className="mt-3 max-w-[58ch] text-[14px]">
          Alles in <code className="text-fg">src/images/projectimg/</code>. Wat geen project meer
          gebruikt wordt nog steeds meegebouwd, dus het loont om het weg te gooien.
        </p>

        <ul className="mt-7 grid gap-2 sm:grid-cols-2 desk:grid-cols-3">
          {covers.map((c) => (
            <li
              key={c.file}
              className={cn(
                "flex items-center gap-3 rounded-brand-sm border p-3",
                c.used ? "border-hair" : "border-line bg-inset",
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-bold text-fg">{c.file}</span>
                <span className="kicker mt-1 block normal-case">
                  {Math.round(c.bytes / 1024)} kB · {c.used ? "in gebruik" : "ongebruikt"}
                </span>
              </span>

              <button
                type="button"
                onClick={() => drop(c.file)}
                aria-label={`${c.file} verwijderen`}
                className={cn(
                  "tap grid size-11 shrink-0 place-items-center rounded-brand-sm border border-line transition-colors",
                  c.used ? "text-muted hover:text-[#C6361A]" : "text-[#C6361A]",
                )}
              >
                <Trash2 aria-hidden className="size-4" strokeWidth={1.75} />
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}


function Row({ project, index, count, onEdit, onMove, onRemove }) {
  const file = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setBusy(true);
    try {
      const id = project.id || slug(project.name);
      const data = await toWebp(picked);
      const res = await fetch("/__studio/cover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, data }),
      });
      const out = await res.json();
      if (res.ok) onEdit({ id, cover: out.cover });
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  /* Deletes the file and clears the field in one go. Doing only one of the two
     is what produces either a card pointing at a missing image or an orphan
     nobody knows about. */
  const dropCover = async () => {
    if (!project.cover) return;
    setBusy(true);
    try {
      await fetch("/__studio/cover", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file: project.cover }),
      });
      onEdit({ cover: "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card pad="md" className="flex flex-col gap-4 desk:flex-row">
      <Reorder index={index} count={count} onMove={onMove} />

      <div className="min-w-0 flex-1">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Naam" value={project.name} onChange={(name) => onEdit({ name })} />
          <Field label="Jaar" value={project.year} onChange={(year) => onEdit({ year })} />
        </div>

        <Field
          className="mt-3"
          label="Link"
          value={project.url}
          placeholder="https://…"
          onChange={(url) => onEdit({ url })}
        />

        <Area
          className="mt-3"
          label="Omschrijving"
          value={project.description}
          onChange={(description) => onEdit({ description })}
        />

        <Field
          className="mt-3"
          label="Tags, gescheiden door komma's"
          value={(project.tags ?? []).join(", ")}
          onChange={(v) => onEdit({ tags: v.split(",").map((t) => t.trim()) })}
        />

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input ref={file} type="file" accept="image/*" onChange={upload} className="hidden" />
          <Action
            variant="ghost"
            size="sm"
            magnetic={false}
            onClick={() => file.current?.click()}
            disabled={busy}
          >
            <ImageIcon aria-hidden className="size-4" strokeWidth={1.75} />
            {busy ? "Bezig…" : project.cover ? "Cover vervangen" : "Cover kiezen"}
          </Action>

          {project.cover && (
            <Action variant="quiet" size="sm" magnetic={false} onClick={dropCover} disabled={busy}>
              <ImageOff aria-hidden className="size-4" strokeWidth={1.75} />
              Cover weg
            </Action>
          )}

          <span className="kicker normal-case">{project.cover || "geen cover"}</span>

          <Action
            variant="quiet"
            size="sm"
            magnetic={false}
            onClick={onRemove}
            className="ml-auto text-[#C6361A]"
          >
            <Trash2 aria-hidden className="size-4" strokeWidth={1.75} />
            Verwijderen
          </Action>
        </div>
      </div>
    </Card>
  );
}

